<template>
  <div ref="containerRef" class="auto-fit-text-container" :style="containerStyle">
    <span ref="textRef" class="auto-fit-text" :style="textStyle">
      {{ text }}
    </span>
  </div>
</template>

<script>
export default {
  name: 'AutoFitMutilComp',
  props: {
    // 显示的文本内容
    text: {
      type: String,
      required: true,
      default: '',
    },
    // 最大字体大小 (rem)
    maxFontSize: {
      type: Number,
      default: 0.64,
    },
    // 最小字体大小 (rem)
    minFontSize: {
      type: Number,
      default: 0.2,
    },
    // 字体减小的步长 (rem)
    step: {
      type: Number,
      default: 0.02,
    },
    // 容器宽度
    width: {
      type: [String, Number],
      default: '100%',
    },
    // 容器高度
    height: {
      type: [String, Number],
      default: 'auto',
    },
    // 是否允许多行显示
    multiLine: {
      type: Boolean,
      default: false,
    },
    // 文本对齐方式
    textAlign: {
      type: String,
      default: 'left',
      validator: (value) => ['left', 'center', 'right', 'justify'].includes(value),
    },
    // 行高倍数
    lineHeight: {
      type: Number,
      default: 1.1,
    },
  },
  data() {
    return {
      currentFontSize: this.maxFontSize,
      resizeObserver: null,
      isResizing: false,
    }
  },
  computed: {
    containerStyle() {
      return {
        width: typeof this.width === 'number' ? `${this.width}px` : this.width,
        height: typeof this.height === 'number' ? `${this.height}px` : this.height,
        overflow: 'hidden',
        position: 'relative',
      }
    },
    textStyle() {
      return {
        fontSize: `${this.currentFontSize}rem`,
        lineHeight: this.lineHeight,
        textAlign: this.textAlign,
        whiteSpace: this.multiLine ? 'normal' : 'nowrap',
        display: 'inline-block',
        width: '100%',
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.adjustFontSize()
      this.setupResizeObserver()
    })
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  },
  watch: {
    text() {
      this.$nextTick(() => {
        this.currentFontSize = this.maxFontSize
        this.adjustFontSize()
      })
    },
    maxFontSize() {
      this.currentFontSize = this.maxFontSize
      this.$nextTick(() => {
        this.adjustFontSize()
      })
    },
    minFontSize() {
      this.$nextTick(() => {
        this.adjustFontSize()
      })
    },
    width() {
      this.$nextTick(() => {
        this.adjustFontSize()
      })
    },
    height() {
      this.$nextTick(() => {
        this.adjustFontSize()
      })
    },
  },
  methods: {
    // 设置ResizeObserver监听容器大小变化
    setupResizeObserver() {
      if (!window.ResizeObserver || !this.$refs.containerRef) return

      this.resizeObserver = new ResizeObserver(() => {
        if (!this.isResizing) {
          this.isResizing = true
          this.$nextTick(() => {
            this.currentFontSize = this.maxFontSize
            this.adjustFontSize()
            this.isResizing = false
          })
        }
      })

      this.resizeObserver.observe(this.$refs.containerRef)
    },

    // 创建临时测量元素
    createMeasurementElement() {
      const element = document.createElement('span')
      element.style.position = 'absolute'
      element.style.visibility = 'hidden'
      element.style.top = '-9999px'
      element.style.left = '-9999px'
      element.style.lineHeight = this.lineHeight
      element.textContent = this.text

      if (this.multiLine) {
        element.style.whiteSpace = 'normal'
        element.style.wordBreak = 'normal'
        element.style.width = this.$refs.containerRef.clientWidth + 'px'
      } else {
        element.style.whiteSpace = 'nowrap'
        element.style.wordBreak = 'normal'
      }

      document.body.appendChild(element)
      return element
    },

    // 调整字体大小
    adjustFontSize() {
      if (!this.$refs.containerRef || !this.$refs.textRef || !this.text) {
        return
      }

      const container = this.$refs.containerRef

      // 获取容器尺寸
      const containerWidth = container.clientWidth
      const containerHeight = container.clientHeight

      // 如果容器没有明确的高度，则只检查宽度
      const checkHeight = this.height !== 'auto' && containerHeight > 0

      // 创建临时测量元素
      const measureElement = this.createMeasurementElement()

      let currentSize = this.maxFontSize

      // 二分查找最适合的字体大小
      let minSize = this.minFontSize
      let maxSize = this.maxFontSize
      let bestSize = minSize

      while (maxSize - minSize > this.step) {
        // 取中间值
        currentSize = Math.floor(((minSize + maxSize) * 10000) / 2) / 10000

        // 设置测量元素的字体大小
        measureElement.style.fontSize = `${currentSize}rem`

        // 获取测量尺寸
        const textWidth = measureElement.scrollWidth
        const textHeight = measureElement.scrollHeight

        // 检查文本是否超出容器
        const widthFits = textWidth <= containerWidth
        const heightFits = !checkHeight || textHeight <= containerHeight

        if (widthFits && heightFits) {
          // 如果文本宽度小于容器宽度，且文本高度小于容器高度，则认为是最适合的字体大小
          // 设置适合字体为当前值，设置最小值为当前值，尝试下一轮寻找
          bestSize = currentSize
          minSize = currentSize
        } else {
          // 如果字体大了，重置最大值，尝试下一轮寻找
          maxSize = currentSize
        }
      }

      // 清理测量元素
      document.body.removeChild(measureElement)

      // 设置最终的字体大小
      this.currentFontSize = Math.max(bestSize, this.minFontSize)

      // 触发自定义事件
      this.$emit('font-size-changed', {
        fontSize: this.currentFontSize,
        isMinSize: this.currentFontSize === this.minFontSize,
      })
    },

    // 手动重新计算字体大小
    recalculate() {
      this.currentFontSize = this.maxFontSize
      this.$nextTick(() => {
        this.adjustFontSize()
      })
    },

    // 获取当前字体大小
    getCurrentFontSize() {
      return this.currentFontSize
    },
  },
}
</script>

<style scoped>
.auto-fit-text-container {
  display: flex;
  align-items: center;
  box-sizing: border-box;
}

.auto-fit-text {
  transition: font-size 0.1s ease;
  margin: 0;
  padding: 0;
}
</style>
